import { enableMapSet, produce } from "immer";
import { atom } from "jotai";

import { Logger } from "@moment/logging";

const logger = new Logger("document-registry");

enableMapSet();

/**
 * The set of document IDs we're interested in loading.
 * This is the source of truth for what documents should be loaded.
 */
export const $registeredDocumentIds = atom(new Set<string>());
$registeredDocumentIds.debugLabel = "registeredDocumentIds";

/**
 * Register interest in a document.
 */
export const $registerDocument = atom(null, (get, set, documentId: string) => {
	const current = get($registeredDocumentIds);
	if (current.has(documentId)) {
		logger.debug("Document already registered", { documentId });
		return;
	}

	logger.info("Registering document", { documentId });
	set(
		$registeredDocumentIds,
		produce(current, (draft) => {
			draft.add(documentId);
		})
	);
});
$registerDocument.debugLabel = "registerDocument";

/**
 * Unregister interest in a document.
 */
export const $unregisterDocument = atom(null, (get, set, documentId: string) => {
	const current = get($registeredDocumentIds);
	if (!current.has(documentId)) {
		logger.debug("Document not registered", { documentId });
		return;
	}

	logger.info("Unregistering document", { documentId });
	set(
		$registeredDocumentIds,
		produce(current, (draft) => {
			draft.delete(documentId);
		})
	);
});
$unregisterDocument.debugLabel = "unregisterDocument";

/**
 * The set of page IDs that have been explicitly requested for editor state loading.
 * Used for on-demand/lazy loading: when a user navigates to a page, the page's editor
 * state is requested via this atom, and the document loader picks it up.
 *
 * Keys are in the format "documentId:pageId".
 */
export const $requestedPageLoads = atom(new Set<string>());
$requestedPageLoads.debugLabel = "requestedPageLoads";

/**
 * Request that a specific page's editor state be loaded.
 * Called by useEditorState when it detects the editor state is null (not yet loaded).
 */
export const $requestPageLoad = atom(
	null,
	(get, set, input: { documentId: string; pageId: string }) => {
		const key = `${input.documentId}:${input.pageId}`;
		const current = get($requestedPageLoads);
		if (current.has(key)) {
			return;
		}

		logger.info("Requesting page load", { documentId: input.documentId, pageId: input.pageId });
		set(
			$requestedPageLoads,
			produce(current, (draft) => {
				draft.add(key);
			})
		);
	}
);
$requestPageLoad.debugLabel = "requestPageLoad";
